// react
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import React from 'react';

// types
import {
	Account,
	PlanningActivityPayload,
	PlanningActivity,
	ConditionPayload,
	Condition,
	V2ContractPayload,
	V2Contract,
	InvoicePayload,
	Invoice,
	WorkPlanPayload,
	WorkPlan,
	ExceptionPayload,
	Exception,
	VoucherPayload,
	Voucher,
} from '../../../@types';

// context
import { ContextProject } from '../Project/Project.context';

// hooks
import { useAccounts } from '../../../hooks/useAccounts';
import { useConditions } from '../../../hooks/useConditions';
import { useExceptions } from '../../../hooks/useExceptions';
import { useInvoices } from '../../../hooks/useInvoices';
import { usePlanningActivities } from '../../../hooks/usePlanningActivities';
import { useV2Contracts } from '../../../hooks/useV2Contracts';
import { useVouchers } from '../../../hooks/useVouchers';
import { useWorkPlans } from '../../../hooks/useWorkPlans';

// services
import { ApiResponse, QueryParams } from '../../../services/api';

// utils
import { getContractAmountPaid, getContractCommitmentAmount } from './Contracts/Contracts.utils';
import { SSENotification, useSSE } from '../../../utils/sse/useSSE';

// Filter Query Types

export interface V2FilterQuery extends QueryParams {
	mode?: string;
	search?: string;
	status?: string;
}

// Dashboard Summary Type

export interface DashboardSummary {
	activityCount: number;
	conditionsPending: number;
	contractCount: number;
	currency: string;
	workPlanCount: number;
	exceptionsCount: number;
	totalAmount: number;
	totalCommitted: number;
	totalPaid: number;
	totalRemaining: number;
	vouchersReady: number;
}

// Context Types

interface SpendPlanContextValue {
	// Data
	accounts: Account[];
	activities: PlanningActivity[];
	conditions: Condition[];
	contracts: V2Contract[];
	invoices: Invoice[];
	workPlans: WorkPlan[];
	exceptions: Exception[];
	vouchers: Voucher[];

	// Loading states
	loading: {
		accounts: boolean;
		activities: boolean;
		conditions: boolean;
		contracts: boolean;
		invoices: boolean;
		workPlans: boolean;
		exceptions: boolean;
		vouchers: boolean;
	};

	// Error states
	errors: {
		accounts: string | null;
		activities: string | null;
		conditions: string | null;
		contracts: string | null;
		invoices: string | null;
		workPlans: string | null;
		exceptions: string | null;
		vouchers: string | null;
	};

	// Pagination
	pagination: {
		activities: ApiResponse<PlanningActivity[]>['pagination'];
		conditions: ApiResponse<Condition[]>['pagination'];
		contracts: ApiResponse<V2Contract[]>['pagination'];
		invoices: ApiResponse<Invoice[]>['pagination'];
		workPlans: ApiResponse<WorkPlan[]>['pagination'];
		exceptions: ApiResponse<Exception[]>['pagination'];
		vouchers: ApiResponse<Voucher[]>['pagination'];
	};

	// Filter queries
	filterQueries: {
		activities: V2FilterQuery;
		conditions: V2FilterQuery;
		contracts: V2FilterQuery;
		invoices: V2FilterQuery;
		workPlans: V2FilterQuery;
		exceptions: V2FilterQuery;
		vouchers: V2FilterQuery;
	};

	// Set filter queries
	setFilterQuery: (
		resource:
			| 'activities'
			| 'conditions'
			| 'contracts'
			| 'invoices'
			| 'workPlans'
			| 'exceptions'
			| 'vouchers',
		query: V2FilterQuery
	) => void;

	// Dashboard summary
	summary: DashboardSummary;
	createWorkPlan: (payload: WorkPlanPayload) => Promise<ApiResponse<WorkPlan>>;
	deactivateWorkPlan: (id: number) => Promise<ApiResponse<WorkPlan>>;
	deleteWorkPlan: (id: number) => Promise<ApiResponse<{ deleted: true }>>;
	updateWorkPlan: (id: number, payload: Partial<WorkPlanPayload>) => Promise<ApiResponse<WorkPlan>>;

	// Actions - Activities
	createActivity: (payload: PlanningActivityPayload) => Promise<ApiResponse<PlanningActivity>>;
	deactivateActivity: (id: number) => Promise<ApiResponse<PlanningActivity>>;
	deleteActivity: (id: number) => Promise<ApiResponse<{ deleted: true }>>;
	updateActivity: (
		id: number,
		payload: Partial<PlanningActivityPayload>
	) => Promise<ApiResponse<PlanningActivity>>;

	// Actions - Contracts
	createContract: (payload: V2ContractPayload) => Promise<ApiResponse<V2Contract>>;
	deactivateContract: (id: number) => Promise<ApiResponse<V2Contract>>;
	deleteContract: (id: number) => Promise<ApiResponse<{ deleted: true }>>;
	updateContract: (
		id: number,
		payload: Partial<V2ContractPayload>
	) => Promise<ApiResponse<V2Contract>>;

	// Actions - Conditions
	createCondition: (payload: ConditionPayload) => Promise<ApiResponse<Condition>>;
	deactivateCondition: (id: number) => Promise<ApiResponse<Condition>>;
	updateCondition: (
		id: number,
		payload: Partial<ConditionPayload>
	) => Promise<ApiResponse<Condition>>;

	// Actions - Exceptions
	createException: (payload: ExceptionPayload) => Promise<ApiResponse<Exception>>;

	// Actions - Vouchers
	createVoucher: (payload: VoucherPayload) => Promise<ApiResponse<Voucher>>;
	updateVoucher: (id: number, payload: Partial<VoucherPayload>) => Promise<ApiResponse<Voucher>>;

	// Actions - Invoices
	createInvoice: (payload: InvoicePayload) => Promise<ApiResponse<Invoice>>;
	deactivateInvoice: (id: number) => Promise<ApiResponse<Invoice>>;
	updateInvoice: (id: number, payload: Partial<InvoicePayload>) => Promise<ApiResponse<Invoice>>;

	// Utility functions
	getActivitiesByWorkPlan: (
		workPlanId: number,
		includeSubActivities?: boolean
	) => PlanningActivity[];
	getConditionsByContract: (contractId: number) => Condition[];
	getContractsByActivity: (activityId: number) => V2Contract[];
	getExceptionByConditionId: (conditionId: number) => Exception | undefined;
	getVouchersByContract: (contractId: number) => Voucher[];
	getInvoicesByContract: (contractId: number) => Invoice[];

	// Refetch functions
	refetchAccounts: () => Promise<void>;
	refetchAll: () => Promise<void>;
	refetchActivities: () => Promise<void>;
	refetchConditions: () => Promise<void>;
	refetchContracts: () => Promise<void>;
	refetchWorkPlans: () => Promise<void>;
	refetchExceptions: () => Promise<void>;
	refetchInvoices: () => Promise<void>;
	refetchVouchers: () => Promise<void>;
}

// Default empty response for actions
const emptyResponse = {
	error: { id: 'NOT_INITIALIZED', message: 'Context not initialized' },
	status: 500,
};

// Context

export const ContextSpendPlan = createContext<SpendPlanContextValue>({
	// Data
	accounts: [],
	activities: [],
	conditions: [],
	contracts: [],
	invoices: [],
	workPlans: [],
	exceptions: [],
	vouchers: [],

	// Loading
	loading: {
		accounts: false,
		activities: false,
		conditions: false,
		contracts: false,
		invoices: false,
		workPlans: false,
		exceptions: false,
		vouchers: false,
	},

	// Errors
	errors: {
		accounts: null,
		activities: null,
		conditions: null,
		contracts: null,
		invoices: null,
		workPlans: null,
		exceptions: null,
		vouchers: null,
	},

	// Pagination
	pagination: {
		activities: undefined,
		conditions: undefined,
		contracts: undefined,
		invoices: undefined,
		workPlans: undefined,
		exceptions: undefined,
		vouchers: undefined,
	},

	// Filter queries
	filterQueries: {
		activities: {},
		conditions: {},
		contracts: {},
		invoices: {},
		workPlans: {},
		exceptions: {},
		vouchers: {},
	},

	// Set filter query
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setFilterQuery: () => {},

	// Summary
	summary: {
		activityCount: 0,
		conditionsPending: 0,
		contractCount: 0,
		currency: 'USD',
		workPlanCount: 0,
		exceptionsCount: 0,
		totalAmount: 0,
		totalCommitted: 0,
		totalPaid: 0,
		totalRemaining: 0,
		vouchersReady: 0,
	},

	// Actions
	createActivity: async () => emptyResponse,
	createCondition: async () => emptyResponse,
	createContract: async () => emptyResponse,
	createWorkPlan: async () => emptyResponse,
	createException: async () => emptyResponse,
	createVoucher: async () => emptyResponse,
	deactivateActivity: async () => emptyResponse,
	deactivateCondition: async () => emptyResponse,
	deactivateContract: async () => emptyResponse,
	deactivateWorkPlan: async () => emptyResponse,
	deleteActivity: async () => emptyResponse,
	deleteContract: async () => emptyResponse,
	deleteWorkPlan: async () => emptyResponse,
	updateActivity: async () => emptyResponse,
	updateCondition: async () => emptyResponse,
	updateContract: async () => emptyResponse,
	updateWorkPlan: async () => emptyResponse,
	updateVoucher: async () => emptyResponse,
	createInvoice: async () => emptyResponse,
	deactivateInvoice: async () => emptyResponse,
	updateInvoice: async () => emptyResponse,

	// Utilities
	getActivitiesByWorkPlan: () => [],
	getConditionsByContract: () => [],
	getContractsByActivity: () => [],
	getExceptionByConditionId: () => undefined,
	getVouchersByContract: () => [],
	getInvoicesByContract: () => [],

	// Refetch
	refetchAccounts: async () => Promise.resolve(),
	refetchActivities: async () => Promise.resolve(),
	refetchAll: async () => Promise.resolve(),
	refetchConditions: async () => Promise.resolve(),
	refetchContracts: async () => Promise.resolve(),
	refetchWorkPlans: async () => Promise.resolve(),
	refetchExceptions: async () => Promise.resolve(),
	refetchInvoices: async () => Promise.resolve(),
	refetchVouchers: async () => Promise.resolve(),
});

// Provider

interface SpendPlanProviderProps {
	children: ReactNode;
}

export const SpendPlanProvider = ({ children }: SpendPlanProviderProps) => {
	// Get project ID from parent context
	const { project } = useContext(ContextProject);
	const projectId = project?.id;

	// Filter query state for each resource
	const [filterQueries, setFilterQueries] = useState<{
		activities: V2FilterQuery;
		conditions: V2FilterQuery;
		contracts: V2FilterQuery;
		invoices: V2FilterQuery;
		workPlans: V2FilterQuery;
		exceptions: V2FilterQuery;
		vouchers: V2FilterQuery;
	}>({
		activities: {},
		conditions: {},
		contracts: {},
		invoices: {},
		workPlans: {},
		exceptions: {},
		vouchers: {},
	});

	// Set filter query for a specific resource
	const setFilterQuery = useCallback(
		(
			resource:
				| 'activities'
				| 'conditions'
				| 'contracts'
				| 'invoices'
				| 'workPlans'
				| 'exceptions'
				| 'vouchers',
			query: V2FilterQuery
		) => {
			setFilterQueries((prev) => ({
				...prev,
				[resource]: query,
				...(resource === 'workPlans' ? { workPlans: query } : {}),
			}));
		},
		[]
	);

	// Hooks for each V2 resource
	const accountsHook = useAccounts({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'balances,entities',
		},
	});

	const workPlansHook = useWorkPlans({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'account,loan,entity,component,category,expenseType',
			...filterQueries.workPlans,
		},
	});

	const activitiesHook = usePlanningActivities({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'workPlan,entity,component,category,expenseType',
			...filterQueries.activities,
		},
	});

	const contractsHook = useV2Contracts({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'activity,supplier',
			...filterQueries.contracts,
		},
	});

	const conditionsHook = useConditions({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'workPlan,activity,contract,document,verifiedBy,component,category,expenseType',
			...filterQueries.conditions,
		},
	});

	const exceptionsHook = useExceptions({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'condition,overriddenBy,notificationRecipient',
			...filterQueries.exceptions,
		},
	});

	const vouchersHook = useVouchers({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'condition,contract,activity,recipientEntity',
			...filterQueries.vouchers,
		},
	});

	const invoicesHook = useInvoices({
		isEnabled: Boolean(projectId),
		projectId,
		query: {
			populate: 'supplier',
			...filterQueries.invoices,
		},
	});

	// Compute dashboard summary from real data
	const summary = useMemo<DashboardSummary>(() => {
		const totalAmount = workPlansHook.data.reduce((sum, e) => sum + (e.amount || 0), 0);
		const defaultWorkPlan = workPlansHook.data[0] || null;
		const totalCommitted = contractsHook.data.reduce(
			(sum, c) => sum + getContractCommitmentAmount(c, defaultWorkPlan),
			0
		);
		const totalPaid = contractsHook.data.reduce((sum, c) => sum + getContractAmountPaid(c), 0);

		return {
			activityCount: activitiesHook.data.length,
			conditionsPending: conditionsHook.data.filter((c) => c.status === 'PENDING').length,
			contractCount: contractsHook.data.length,
			currency: workPlansHook.data[0]?.currency || 'USD',
			workPlanCount: workPlansHook.data.length,
			exceptionsCount: exceptionsHook.data.length,
			totalAmount,
			totalCommitted,
			totalPaid,
			totalRemaining: totalCommitted - totalPaid,
			vouchersReady: vouchersHook.data.filter((v) => v.status === 'READY').length,
		};
	}, [
		activitiesHook.data,
		conditionsHook.data,
		contractsHook.data,
		workPlansHook.data,
		exceptionsHook.data,
		vouchersHook.data,
	]);

	// Utility functions
	const getActivitiesByWorkPlan = useCallback(
		(workPlanId: number, includeSubActivities = false) => {
			// First, get all activities in this work plan
			const inWorkPlan = activitiesHook.data.filter((a) => {
				const wp = typeof a.workPlan === 'number' ? a.workPlan : a.workPlan?.id;
				return wp === workPlanId;
			});

			if (includeSubActivities) return inWorkPlan;

			// Only exclude sub-activities whose parent is also in this work plan
			const workPlanActivityIds = new Set(inWorkPlan.map((a) => a.id));
			return inWorkPlan.filter((a) => {
				const parentId = typeof a.parent === 'number' ? a.parent : a.parent?.id;
				return !parentId || !workPlanActivityIds.has(parentId);
			});
		},
		[activitiesHook.data]
	);

	const getContractsByActivity = useCallback(
		(activityId: number) => {
			return contractsHook.data.filter((c) => c.planningActivity === activityId);
		},
		[contractsHook.data]
	);

	const getConditionsByContract = useCallback(
		(contractId: number) => {
			return conditionsHook.data.filter((c) => {
				const contract = typeof c.contract === 'number' ? c.contract : c.contract?.id;
				return contract === contractId;
			});
		},
		[conditionsHook.data]
	);

	const getVouchersByContract = useCallback(
		(contractId: number) => {
			return vouchersHook.data.filter((v) => {
				const contract = typeof v.contract === 'number' ? v.contract : v.contract?.id;
				return contract === contractId;
			});
		},
		[vouchersHook.data]
	);

	const getInvoicesByContract = useCallback(
		(contractId: number) => {
			return invoicesHook.data.filter((inv) => {
				const contract = typeof inv.contract === 'number' ? inv.contract : inv.contract?.id;
				return contract === contractId;
			});
		},
		[invoicesHook.data]
	);

	const getExceptionByConditionId = useCallback(
		(conditionId: number) => {
			return exceptionsHook.data.find((e) => {
				const condition = typeof e.condition === 'number' ? e.condition : e.condition?.id;
				return condition === conditionId;
			});
		},
		[exceptionsHook.data]
	);

	// Refetch all data
	const refetchAll = useCallback(async () => {
		await Promise.all([
			accountsHook.refetch(),
			workPlansHook.refetch(),
			activitiesHook.refetch(),
			contractsHook.refetch(),
			conditionsHook.refetch(),
			exceptionsHook.refetch(),
			vouchersHook.refetch(),
			invoicesHook.refetch(),
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refetch fns are stable; deps list is intentionally just the .refetch refs
	}, [
		accountsHook.refetch,
		activitiesHook.refetch,
		conditionsHook.refetch,
		contractsHook.refetch,
		workPlansHook.refetch,
		exceptionsHook.refetch,
		vouchersHook.refetch,
		invoicesHook.refetch,
	]);

	// Live updates: when a maker-checker status change for this project arrives
	// over SSE, refetch only the affected resource so the open list/detail page
	// rerenders without a manual refresh.
	//
	// bldt-notifications stringifies the POSTed `message` field and exposes it
	// back as `payload.message` (parsed when it's valid JSON). All v2 status
	// events ride that channel — see bldt-ui-fn/common/services/sseNotify.ts.
	const sseFilter = useCallback(
		(n: SSENotification) => {
			if (typeof n.type !== 'string' || !n.type.startsWith('v2:')) {
				return false;
			}
			const data = n.payload?.message;
			if (!data || typeof data !== 'object') {
				return false;
			}
			return Number((data as Record<string, unknown>).projectId) === projectId;
		},
		[projectId]
	);

	const onSseNotification = useCallback(
		(n: SSENotification) => {
			const data = n.payload?.message as Record<string, unknown> | undefined;
			const resource = typeof data?.resource === 'string' ? data.resource : '';
			switch (resource) {
				case 'contract':
					void contractsHook.refetch();
					// conditions reference contracts; refresh both so condition lists update too
					void conditionsHook.refetch();
					break;
				case 'invoice':
					void invoicesHook.refetch();
					break;
				case 'voucher':
					void vouchersHook.refetch();
					break;
				case 'workplan':
					void workPlansHook.refetch();
					// activities + spend totals roll up to the work plan
					void activitiesHook.refetch();
					break;
				case 'spendplan':
					// spend plan changes can touch every resource
					void refetchAll();
					break;
				case 'condition':
					void conditionsHook.refetch();
					void exceptionsHook.refetch();
					break;
				default:
					// unknown v2 resource — fall back to a full refresh
					void refetchAll();
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refetch fns are stable; deps list is intentionally just the .refetch refs
		[
			activitiesHook.refetch,
			conditionsHook.refetch,
			contractsHook.refetch,
			exceptionsHook.refetch,
			invoicesHook.refetch,
			refetchAll,
			vouchersHook.refetch,
			workPlansHook.refetch,
		]
	);

	useSSE({ filter: sseFilter, onNotification: onSseNotification });

	const value: SpendPlanContextValue = {
		// Data
		accounts: accountsHook.data,
		activities: activitiesHook.data,
		conditions: conditionsHook.data,
		contracts: contractsHook.data,
		invoices: invoicesHook.data,
		workPlans: workPlansHook.data,
		exceptions: exceptionsHook.data,
		vouchers: vouchersHook.data,

		// Loading
		loading: {
			accounts: accountsHook.loading,
			activities: activitiesHook.loading,
			conditions: conditionsHook.loading,
			contracts: contractsHook.loading,
			invoices: invoicesHook.loading,
			workPlans: workPlansHook.loading,
			exceptions: exceptionsHook.loading,
			vouchers: vouchersHook.loading,
		},

		// Errors
		errors: {
			accounts: accountsHook.error,
			activities: activitiesHook.error,
			conditions: conditionsHook.error,
			contracts: contractsHook.error,
			invoices: invoicesHook.error,
			workPlans: workPlansHook.error,
			exceptions: exceptionsHook.error,
			vouchers: vouchersHook.error,
		},

		// Pagination
		pagination: {
			activities: activitiesHook.pagination,
			conditions: conditionsHook.pagination,
			contracts: contractsHook.pagination,
			invoices: invoicesHook.pagination,
			workPlans: workPlansHook.pagination,
			exceptions: exceptionsHook.pagination,
			vouchers: vouchersHook.pagination,
		},

		// Filter queries
		filterQueries,

		// Set filter query
		setFilterQuery,

		// Summary
		summary,

		// Actions - WorkPlans
		createWorkPlan: workPlansHook.create,
		deactivateWorkPlan: workPlansHook.deactivate,
		deleteWorkPlan: workPlansHook.delete,
		updateWorkPlan: workPlansHook.update,

		// Actions - Activities
		createActivity: activitiesHook.create,
		deactivateActivity: activitiesHook.deactivate,
		deleteActivity: activitiesHook.delete,
		updateActivity: activitiesHook.update,

		// Actions - Contracts
		createContract: contractsHook.create,
		deactivateContract: contractsHook.deactivate,
		deleteContract: contractsHook.delete,
		updateContract: contractsHook.update,

		// Actions - Conditions
		createCondition: conditionsHook.create,
		deactivateCondition: conditionsHook.deactivate,
		updateCondition: conditionsHook.update,

		// Actions - Exceptions
		createException: exceptionsHook.create,

		// Actions - Vouchers
		createVoucher: vouchersHook.create,
		updateVoucher: vouchersHook.update,

		// Actions - Invoices
		createInvoice: invoicesHook.create,
		deactivateInvoice: invoicesHook.deactivate,
		updateInvoice: invoicesHook.update,

		// Utilities
		getActivitiesByWorkPlan,
		getConditionsByContract,
		getContractsByActivity,
		getExceptionByConditionId,
		getVouchersByContract,
		getInvoicesByContract,

		// Refetch
		refetchAccounts: accountsHook.refetch,
		refetchActivities: activitiesHook.refetch,
		refetchAll,
		refetchConditions: conditionsHook.refetch,
		refetchContracts: contractsHook.refetch,
		refetchWorkPlans: workPlansHook.refetch,
		refetchExceptions: exceptionsHook.refetch,
		refetchInvoices: invoicesHook.refetch,
		refetchVouchers: vouchersHook.refetch,
	};

	return React.createElement(ContextSpendPlan.Provider, { value }, children);
};
